import type { Attribute, AttributeType, ID } from "@/lib/catalog/types";
import { normalizeVisibilityRule } from "@/lib/catalog/attributeVisibility";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface CatalogAttributeRecord extends Attribute {
  attributeGroupId: ID;
  createdAt: string;
}

const LS_KEY = "inventory.catalogAttributes.v1";

type CatalogAttributeApiRecord = {
  id: string;
  attribute_group_id: string;
  code: string;
  name: string;
  type: AttributeType;
  is_required?: boolean;
  is_listing?: boolean;
  is_filter?: boolean;
  is_invoice?: boolean;
  is_delivery_checklist?: boolean;
  allow_runtime_add?: boolean;
  sort_order?: number;
  options?: Array<{
    id?: string;
    label: string;
    value: string;
    sort_order?: number;
  }>;
  accept?: string | null;
  max_size_mb?: number | null;
  max_files?: number | null;
  parent_attribute_id?: string | number | null;
  visibility_rule?: unknown;
  created_at?: string;
};

type ListCatalogAttributesApiResponse = {
  data?: CatalogAttributeApiRecord[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
  };
};

type UpsertCatalogAttributeApiResponse = {
  message?: string;
  data?: CatalogAttributeApiRecord;
};

type DeleteCatalogAttributeApiResponse = {
  message?: string;
};

type UpsertCatalogAttributeResult = {
  record: CatalogAttributeRecord;
  message: string;
};

type DeleteCatalogAttributeResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: CatalogAttributeRecord[]): CatalogAttributeRecord[] {
  const byId = new Map<ID, CatalogAttributeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: CatalogAttributeApiRecord): CatalogAttributeRecord {
  return {
    id: record.id,
    attributeGroupId: record.attribute_group_id,
    code: record.code,
    name: record.name,
    type: record.type,
    isRequired: Boolean(record.is_required),
    isListing: Boolean(record.is_listing),
    isFilter: Boolean(record.is_filter),
    isInvoice: Boolean(record.is_invoice),
    isDeliveryChecklist: Boolean(record.is_delivery_checklist),
    isGatePass: Boolean(record.is_gate_pass),
    allowRuntimeAdd: Boolean(record.allow_runtime_add),
    sortOrder: record.sort_order ?? 0,
    options: record.options?.map((option, idx) => ({
      id: option.id || crypto.randomUUID(),
      label: option.label,
      value: option.value,
      sortOrder: option.sort_order ?? idx,
    })),
    accept: record.accept ?? undefined,
    maxSizeMb: record.max_size_mb ?? undefined,
    maxFiles: record.max_files ?? undefined,
    parentAttributeId:
      record.parent_attribute_id == null || record.parent_attribute_id === ""
        ? null
        : String(record.parent_attribute_id),
    visibilityRule: normalizeVisibilityRule(record.visibility_rule),
    createdAt: record.created_at || new Date().toISOString(),
  };
}

function buildAttributePayload(data: {
  attributeGroupId: ID;
  code: string;
  name: string;
  type: AttributeType;
  isRequired?: boolean;
  isListing?: boolean;
  isFilter?: boolean;
  isInvoice?: boolean;
  isDeliveryChecklist?: boolean;
  isGatePass?: boolean;
  allowRuntimeAdd?: boolean;
  sortOrder?: number;
  options?: Attribute["options"];
  accept?: string;
  maxSizeMb?: number;
  maxFiles?: number;
  parentAttributeId?: ID | null;
  visibilityRule?: Attribute["visibilityRule"];
}) {
  return {
    attribute_group_id: data.attributeGroupId,
    code: slugify(data.code),
    name: data.name.trim(),
    type: data.type,
    is_required: Boolean(data.isRequired),
    is_listing: Boolean(data.isListing),
    is_filter: Boolean(data.isFilter),
    is_invoice: Boolean(data.isInvoice),
    is_delivery_checklist: Boolean(data.isDeliveryChecklist),
    is_gate_pass: Boolean(data.isGatePass),
    allow_runtime_add: Boolean(data.allowRuntimeAdd),
    sort_order: data.sortOrder ?? 0,
    parent_attribute_id: data.parentAttributeId ?? null,
    visibility_rule: data.visibilityRule
      ? {
          operator: data.visibilityRule.operator,
          parent_option_values: data.visibilityRule.parentOptionValues,
        }
      : null,
    options: data.options?.map((option, idx) => ({
      id: option.id,
      label: option.label,
      value: option.value,
      sort_order: option.sortOrder ?? idx,
    })),
    accept: data.accept,
    max_size_mb: data.maxSizeMb,
    max_files: data.maxFiles,
  };
}

function load(): CatalogAttributeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CatalogAttributeRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: CatalogAttributeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listCatalogAttributeRecords(): CatalogAttributeRecord[] {
  return load().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
}

export function listCatalogAttributesByGroup(groupId: ID): CatalogAttributeRecord[] {
  return load()
    .filter((a) => a.attributeGroupId === groupId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function getCatalogAttributeById(id: ID): CatalogAttributeRecord | undefined {
  return load().find((a) => a.id === id);
}

export function codeExists(code: string, exceptId?: ID): boolean {
  const c = slugify(code);
  return load().some((a) => slugify(a.code) === c && a.id !== exceptId);
}

export async function syncCatalogAttributesFromApi() {
  const response = await api.get<ListCatalogAttributesApiResponse>("/api/v1/catalog/attributes", {
    params: {
      page: 1,
      per_page: 200,
    },
  });
  const records = (response.data?.data ?? []).map(mapApiRecord);
  save(records);
  return listCatalogAttributeRecords();
}

export async function createCatalogAttribute(data: {
  attributeGroupId: ID;
  code: string;
  name: string;
  type: AttributeType;
  isRequired?: boolean;
  isListing?: boolean;
  isFilter?: boolean;
  sortOrder?: number;
  options?: Attribute["options"];
  accept?: string;
  maxSizeMb?: number;
  maxFiles?: number;
  parentAttributeId?: ID | null;
  visibilityRule?: Attribute["visibilityRule"];
  allowRuntimeAdd?: boolean;
}): Promise<UpsertCatalogAttributeResult> {
  const items = load();
  const payload = buildAttributePayload(data);
  const response = await api.post<UpsertCatalogAttributeApiResponse>("/api/v1/catalog/attributes", payload);
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create attribute response.");
  }
  const record = mapApiRecord(created);
  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Attribute created successfully.",
  };
}

export async function updateCatalogAttribute(
  id: ID,
  data: {
    attributeGroupId: ID;
    code: string;
    name: string;
    type: AttributeType;
    isRequired?: boolean;
    isListing?: boolean;
    isFilter?: boolean;
    sortOrder?: number;
    options?: Attribute["options"];
    accept?: string;
    maxSizeMb?: number;
    maxFiles?: number;
    parentAttributeId?: ID | null;
    visibilityRule?: Attribute["visibilityRule"];
    allowRuntimeAdd?: boolean;
  },
): Promise<UpsertCatalogAttributeResult | null> {
  const items = load();
  const i = items.findIndex((a) => a.id === id);
  if (i === -1) return null;
  const payload = buildAttributePayload(data);
  const response = await api.put<UpsertCatalogAttributeApiResponse>(`/api/v1/catalog/attributes/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update attribute response.");
  }
  items[i] = mapApiRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Attribute updated successfully.",
  };
}

export async function deleteCatalogAttribute(id: ID): Promise<DeleteCatalogAttributeResult> {
  const response = await api.delete<DeleteCatalogAttributeApiResponse>(`/api/v1/catalog/attributes/${id}`);
  const items = load();
  const nextItems = items.filter((a) => a.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Attribute deleted successfully.",
  };
}

export function replaceAllCatalogAttributes(records: CatalogAttributeRecord[]) {
  save(records);
}

export function attributeCountForGroup(groupId: ID): number {
  return load().filter((a) => a.attributeGroupId === groupId).length;
}
